import React, { useContext, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardGroup,
  Col,
  Container,
  Form,
  Modal,
  ProgressBar,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { Link, useHistory } from 'react-router-dom';
import { notify } from 'react-notify-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faExclamationTriangle,
  faFileCsv,
  faPlay,
  faForward,
  faStopCircle,
  faTimesCircle,
  faUpload,
} from '@fortawesome/free-solid-svg-icons';
import { CountryDropdown, RegionDropdown } from 'react-country-region-selector';
import { ClassroomList } from '../../Context/ClassroomListContext';
import CourseCard from './subcomponents/CourseCard';
import { createStudent } from '../../services/studentsService';
import {
  MANDATORY_IMPORT_FIELDS,
  OPTIONAL_IMPORT_FIELDS,
  autoDetectFieldMapping,
  buildStudentImportPayload,
  extractCsvStructure,
  mapCsvRowToStudent,
  parseCsvGrid,
} from '../../helpers/importStudentsCsv';
import './studentCreateStyles.css';
import './studentImportCsv.css';

export function StudentImportCsv() {
  const history = useHistory();
  const classroomContext = useContext(ClassroomList);
  const fileInputRef = useRef(null);
  const failureDecisionResolverRef = useRef(null);

  const currentYear = new Date().getFullYear();

  const [fileName, setFileName] = useState('');
  const [rawGrid, setRawGrid] = useState([]);
  const [hasHeaderRow, setHasHeaderRow] = useState(true);
  const [csvColumns, setCsvColumns] = useState([]);
  const [csvDataRows, setCsvDataRows] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({
    firstName: '',
    lastName: '',
    email: '',
    dni: '',
    phone: '',
  });

  const [delaySeconds, setDelaySeconds] = useState(5);
  const [classroomYear, setClassroomYear] = useState(currentYear);
  const [country, setCountry] = useState('Argentina');
  const [region, setRegion] = useState('Tucumán');
  const [additionalNotes, setAdditionalNotes] = useState('');

  const [courseQuantity, setCourseQuantity] = useState(1);
  const [courseConfig, setCourseConfig] = useState({
    course: [],
    classroom: ['', '', ''],
    monthlyInstallments: [],
    temporalInstallmentQuantity: '',
    price: [],
    isActive: ['up', '', ''],
    scholarship: [false],
    downReason: ['', '', ''],
    otherDownReason: ['', '', ''],
    moduleDownReason: ['', '', ''],
    commissionChangedReason: ['', '', ''],
    otherCommissionChangedReason: ['', '', ''],
    moduleCommissionChangedReason: ['', '', ''],
    changedInCommission: ['', '', ''],
    currency: ['ARS', '', ''],
    plan: [],
  });

  const [isImporting, setIsImporting] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalToProcess, setTotalToProcess] = useState(0);
  const [currentStudentLabel, setCurrentStudentLabel] = useState('');
  const [statusPhaseText, setStatusPhaseText] = useState('');
  const [failedRowModal, setFailedRowModal] = useState(null);
  const [importResults, setImportResults] = useState([]);
  const [importFinished, setImportFinished] = useState(false);
  const [wasCancelled, setWasCancelled] = useState(false);

  const applyParsedGrid = (gridData, useHeaders) => {
    const { columns, dataRows } = extractCsvStructure(gridData, useHeaders);
    setCsvColumns(columns);
    setCsvDataRows(dataRows);
    if (useHeaders) {
      setFieldMapping(autoDetectFieldMapping(columns));
    } else {
      setFieldMapping({
        firstName: columns[0]?.index ?? '',
        lastName: columns[1]?.index ?? '',
        email: columns[2]?.index ?? '',
        dni: columns[3]?.index ?? '',
        phone: columns[4]?.index ?? '',
      });
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files && event.target.files[0];
    if (!selectedFile) {
      return;
    }

    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      const textContent = loadEvent.target?.result;
      if (typeof textContent !== 'string') {
        notify.show('No se pudo leer el archivo CSV', 'error');
        return;
      }
      const parsedGrid = parseCsvGrid(textContent);
      if (parsedGrid.length === 0) {
        notify.show('El archivo CSV está vacío o no tiene filas válidas', 'warning');
        return;
      }
      setFileName(selectedFile.name);
      setRawGrid(parsedGrid);
      setImportResults([]);
      setImportFinished(false);
      setWasCancelled(false);
      applyParsedGrid(parsedGrid, hasHeaderRow);
    };
    reader.onerror = () => {
      notify.show('Error al leer el archivo CSV', 'error');
    };
    reader.readAsText(selectedFile, 'UTF-8');
  };

  const handleHeaderToggle = (event) => {
    const checked = event.target.checked;
    setHasHeaderRow(checked);
    if (rawGrid.length > 0) {
      applyParsedGrid(rawGrid, checked);
    }
  };

  const handleMappingChange = (fieldKey, columnIndex) => {
    setFieldMapping((previousMapping) => ({
      ...previousMapping,
      [fieldKey]: columnIndex,
    }));
  };

  const handleCardSave = (cardData, cardIndex) => {
    const activeClassroomList = classroomContext?.crList || [];
    const selectedClassroomObj = activeClassroomList.find(
      (classroomItem) => classroomItem.classroom === cardData.classroom
    );

    setCourseConfig((previousConfig) => {
      const nextCourses = [...previousConfig.course];
      const nextClassrooms = [...previousConfig.classroom];
      const nextInstallments = [...previousConfig.monthlyInstallments];
      const nextPrices = [...previousConfig.price];
      const nextIsActive = [...previousConfig.isActive];
      const nextScholarships = [...previousConfig.scholarship];
      const nextDownReason = [...previousConfig.downReason];
      const nextOtherDownReason = [...previousConfig.otherDownReason];
      const nextModuleDownReason = [...previousConfig.moduleDownReason];
      const nextCommissionChangedReason = [
        ...previousConfig.commissionChangedReason,
      ];
      const nextOtherCommissionChangedReason = [
        ...previousConfig.otherCommissionChangedReason,
      ];
      const nextModuleCommissionChangedReason = [
        ...previousConfig.moduleCommissionChangedReason,
      ];
      const nextChangedInCommission = [...previousConfig.changedInCommission];
      const nextCurrencies = [...previousConfig.currency];
      const nextPlans = [...previousConfig.plan];

      nextCourses[cardIndex] = cardData.course || null;
      nextClassrooms[cardIndex] = cardData.classroom;
      nextInstallments[cardIndex] = cardData.monthlyInstallments;
      nextPrices[cardIndex] = cardData.price;
      nextIsActive[cardIndex] = cardData.isActive;
      nextScholarships[cardIndex] = Boolean(cardData.scholarship);
      nextDownReason[cardIndex] = cardData.downReason;
      nextOtherDownReason[cardIndex] = cardData.otherDownReason;
      nextModuleDownReason[cardIndex] = cardData.moduleDownReason;
      nextCommissionChangedReason[cardIndex] = cardData.commissionChangedReason;
      nextOtherCommissionChangedReason[cardIndex] =
        cardData.otherCommissionChangedReason;
      nextModuleCommissionChangedReason[cardIndex] =
        cardData.moduleCommissionChangedReason;
      nextChangedInCommission[cardIndex] = cardData.changedInCommission;
      nextCurrencies[cardIndex] = cardData.currency;

      if (selectedClassroomObj?._id) {
        nextPlans[cardIndex] = {
          planId: cardData?.plan || cardData.planes?.[0]?._id,
          classroom: selectedClassroomObj.classroom,
          classroomId: selectedClassroomObj._id,
        };
      }

      return {
        course: nextCourses,
        classroom: nextClassrooms,
        monthlyInstallments: nextInstallments,
        temporalInstallmentQuantity: cardData.monthlyInstallments,
        price: nextPrices,
        isActive: nextIsActive,
        scholarship: nextScholarships,
        downReason: nextDownReason,
        otherDownReason: nextOtherDownReason,
        moduleDownReason: nextModuleDownReason,
        commissionChangedReason: nextCommissionChangedReason,
        otherCommissionChangedReason: nextOtherCommissionChangedReason,
        moduleCommissionChangedReason: nextModuleCommissionChangedReason,
        changedInCommission: nextChangedInCommission,
        currency: nextCurrencies,
        plan: nextPlans,
      };
    });
  };

  const handleCardClose = (indexToRemove) => {
    if (courseQuantity === 1) {
      notify.show('Tiene que existir al menos una comisión', 'error');
      return;
    }

    setCourseQuantity((previousQty) => previousQty - 1);
    setCourseConfig((previousConfig) => {
      const removeAtIndex = (list) => {
        const copy = [...list];
        copy.splice(indexToRemove, 1);
        return copy;
      };

      return {
        ...previousConfig,
        course: removeAtIndex(previousConfig.course),
        classroom: removeAtIndex(previousConfig.classroom),
        monthlyInstallments: removeAtIndex(previousConfig.monthlyInstallments),
        price: removeAtIndex(previousConfig.price),
        isActive: removeAtIndex(previousConfig.isActive),
        scholarship: removeAtIndex(previousConfig.scholarship),
        plan: removeAtIndex(previousConfig.plan),
      };
    });
  };

  const handleToggleShowFinished = (showFinished) => {
    if (classroomContext?.updateClassRoomList) {
      classroomContext.updateClassRoomList(showFinished);
    }
  };

  const mappedRows = csvDataRows.map((rowCells, rowIdx) =>
    mapCsvRowToStudent(
      rowCells,
      fieldMapping,
      hasHeaderRow ? rowIdx + 2 : rowIdx + 1
    )
  );

  const mandatoryFieldsMapped =
    fieldMapping.firstName !== '' &&
    fieldMapping.lastName !== '' &&
    fieldMapping.email !== '';

  const hasSelectedClassroom = courseConfig.classroom
    .slice(0, courseQuantity)
    .every((classroomName) => Boolean(classroomName));

  const hasSelectedActiveState = courseConfig.isActive
    .slice(0, courseQuantity)
    .every((activeState) => Boolean(activeState));

  const hasSelectedInstallments = courseConfig.monthlyInstallments
    .slice(0, courseQuantity)
    .every((installmentCount) => Number(installmentCount) >= 1);

  const canStartImport =
    !isImporting &&
    mappedRows.length > 0 &&
    mandatoryFieldsMapped &&
    hasSelectedClassroom &&
    hasSelectedActiveState &&
    hasSelectedInstallments;

  const waitDelay = (milliseconds) =>
    new Promise((resolve) => {
      setTimeout(resolve, milliseconds);
    });

  const askUserOnRowFailure = (failureInfo) =>
    new Promise((resolve) => {
      failureDecisionResolverRef.current = resolve;
      setFailedRowModal(failureInfo);
    });

  const handleFailureModalDecision = (decision) => {
    setFailedRowModal(null);
    if (failureDecisionResolverRef.current) {
      const resolveDecision = failureDecisionResolverRef.current;
      failureDecisionResolverRef.current = null;
      resolveDecision(decision);
    }
  };

  /**
   * Sequentially creates each mapped student via POST /api/students with the configured delay and interactive failure recovery.
   * @param {Event} event form submit event
   * @returns {Promise<void>} resolves when all rows complete or user cancels
   */
  const handleStartImport = async (event) => {
    if (event) {
      event.preventDefault();
    }

    if (!mandatoryFieldsMapped) {
      notify.show(
        'Debes mapear los campos obligatorios: Nombre, Apellido y E-Mail',
        'warning'
      );
      return;
    }

    if (!hasSelectedClassroom || !hasSelectedActiveState || !hasSelectedInstallments) {
      notify.show(
        'Completa la comisión, el plan/cuotas y el estado en la tarjeta de curso',
        'warning'
      );
      return;
    }

    const totalRows = mappedRows.length;
    if (totalRows === 0) {
      notify.show('No hay filas para importar en el CSV', 'warning');
      return;
    }

    setIsImporting(true);
    setImportFinished(false);
    setWasCancelled(false);
    setProcessedCount(0);
    setTotalToProcess(totalRows);
    setImportResults([]);

    const accumulatedResults = [];
    const safeDelaySeconds = Math.max(0, Number(delaySeconds) || 0);

    for (let rowIndex = 0; rowIndex < totalRows; rowIndex += 1) {
      const currentStudent = mappedRows[rowIndex];
      const fullName = `${currentStudent.firstName} ${currentStudent.lastName}`.trim();
      setCurrentStudentLabel(
        `${fullName || 'Sin nombre'} (${currentStudent.email || 'sin email'})`
      );
      setStatusPhaseText(
        `Creando estudiante ${rowIndex + 1} de ${totalRows}...`
      );

      let rowFailed = false;
      let failureMessage = '';
      let createdStudentId = null;

      if (!currentStudent.isValid) {
        rowFailed = true;
        failureMessage = `Campos obligatorios faltantes: ${currentStudent.missingFields.join(', ')}`;
      } else {
        const studentPayload = buildStudentImportPayload(currentStudent, {
          ...courseConfig,
          courseQuantity,
          classroomYear,
          country,
          region,
          additionalNotes,
        });

        try {
          const responseData = await createStudent(studentPayload);
          if (responseData && responseData.success) {
            createdStudentId = responseData.id || null;
            failureMessage = responseData.message || 'Alumno creado correctamente';
          } else {
            rowFailed = true;
            failureMessage =
              responseData?.message ||
              'El servidor rechazó la creación del alumno';
          }
        } catch (requestError) {
          rowFailed = true;
          failureMessage =
            requestError?.response?.data?.message ||
            requestError?.message ||
            'Error de red al crear el alumno';
        }
      }

      if (!rowFailed) {
        const successResult = {
          rowNumber: currentStudent.rowNumber,
          firstName: currentStudent.firstName,
          lastName: currentStudent.lastName,
          email: currentStudent.email,
          dni: currentStudent.dni,
          phone: currentStudent.phone,
          status: 'created',
          message: failureMessage,
          studentId: createdStudentId,
        };
        accumulatedResults.push(successResult);
        setImportResults([...accumulatedResults]);
        setProcessedCount(rowIndex + 1);
      } else {
        const userDecision = await askUserOnRowFailure({
          rowNumber: currentStudent.rowNumber,
          firstName: currentStudent.firstName,
          lastName: currentStudent.lastName,
          email: currentStudent.email,
          errorMessage: failureMessage,
          currentIndex: rowIndex + 1,
          totalRows,
        });

        const failedResult = {
          rowNumber: currentStudent.rowNumber,
          firstName: currentStudent.firstName,
          lastName: currentStudent.lastName,
          email: currentStudent.email,
          dni: currentStudent.dni,
          phone: currentStudent.phone,
          status: userDecision === 'skip' ? 'skipped' : 'failed',
          message: failureMessage,
          studentId: null,
        };
        accumulatedResults.push(failedResult);
        setImportResults([...accumulatedResults]);
        setProcessedCount(rowIndex + 1);

        if (userDecision === 'cancel') {
          for (
            let remainingIndex = rowIndex + 1;
            remainingIndex < totalRows;
            remainingIndex += 1
          ) {
            const remainingStudent = mappedRows[remainingIndex];
            accumulatedResults.push({
              rowNumber: remainingStudent.rowNumber,
              firstName: remainingStudent.firstName,
              lastName: remainingStudent.lastName,
              email: remainingStudent.email,
              dni: remainingStudent.dni,
              phone: remainingStudent.phone,
              status: 'cancelled',
              message: 'Operación cancelada por el usuario',
              studentId: null,
            });
          }
          setImportResults([...accumulatedResults]);
          setWasCancelled(true);
          setIsImporting(false);
          setImportFinished(true);
          setStatusPhaseText('Importación cancelada por el usuario.');
          notify.show('Importación cancelada', 'warning');
          return;
        }
      }

      const isLastRow = rowIndex === totalRows - 1;
      if (!isLastRow && safeDelaySeconds > 0) {
        setStatusPhaseText(
          `Esperando ${safeDelaySeconds} segundos antes del próximo registro...`
        );
        await waitDelay(safeDelaySeconds * 1000);
      }
    }

    setIsImporting(false);
    setImportFinished(true);
    setStatusPhaseText('Proceso de importación finalizado.');
    notify.show('Proceso de importación finalizado', 'success');
  };

  const activeClassrooms = classroomContext?.crList || [];
  const cardIndices = [];
  for (let cardIndex = 0; cardIndex < courseQuantity; cardIndex += 1) {
    cardIndices.push(cardIndex);
  }

  const createdCount = importResults.filter(
    (resultItem) => resultItem.status === 'created'
  ).length;
  const failedOrSkippedCount = importResults.filter(
    (resultItem) =>
      resultItem.status === 'failed' || resultItem.status === 'skipped'
  ).length;
  const cancelledCount = importResults.filter(
    (resultItem) => resultItem.status === 'cancelled'
  ).length;

  const progressPercentage =
    totalToProcess > 0
      ? Math.round((processedCount / totalToProcess) * 100)
      : 0;

  return (
    <Container className="mb-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="mb-1">
            <FontAwesomeIcon icon={faFileCsv} className="text-success mr-2" />
            Importar alumnos desde CSV
          </h3>
          <small className="text-muted">
            Selecciona un archivo CSV, mapea las columnas y asigna la comisión y plan para todos los alumnos importados.
          </small>
        </div>
        <Button
          variant="outline-secondary"
          onClick={() => history.push('/students/viewAll')}
          disabled={isImporting}
        >
          Volver al listado
        </Button>
      </div>

      <Form onSubmit={handleStartImport} autoComplete="off">
        <Card className="shadow-sm mb-4">
          <Card.Body>
            <h5 className="mb-3">1. Archivo CSV y mapeo de campos</h5>
            <Row className="align-items-center">
              <Col md={7}>
                <div className="import-csv-dropzone p-3">
                  <Form.Group controlId="csvFileInput" className="mb-2">
                    <Form.Label className="font-weight-bold d-block">
                      Archivo CSV<strong className="text-danger">*</strong>
                    </Form.Label>
                    <Form.Control
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      onChange={handleFileChange}
                      disabled={isImporting}
                      className="d-none"
                    />
                    <div className="d-flex flex-wrap align-items-center">
                      <Button
                        variant="info"
                        type="button"
                        onClick={() =>
                          fileInputRef.current && fileInputRef.current.click()
                        }
                        disabled={isImporting}
                        className="mr-3 mb-2 mb-sm-0"
                      >
                        <FontAwesomeIcon icon={faUpload} className="mr-2" />
                        Seleccionar archivo
                      </Button>
                      <span className="text-muted small text-truncate">
                        {fileName || 'Ningún archivo seleccionado'}
                      </span>
                    </div>
                  </Form.Group>
                  <Form.Check
                    type="checkbox"
                    id="csvHasHeadersCheck"
                    label="La primera fila contiene encabezados de columna"
                    checked={hasHeaderRow}
                    onChange={handleHeaderToggle}
                    disabled={isImporting}
                  />
                </div>
              </Col>
              <Col md={5} className="mt-3 mt-md-0">
                {fileName ? (
                  <Alert variant="info" className="mb-0">
                    <div>
                      <strong>Archivo cargado:</strong> {fileName}
                    </div>
                    <div>
                      <strong>Filas de alumnos detectadas:</strong>{' '}
                      {mappedRows.length}
                    </div>
                    <div>
                      <strong>Columnas detectadas:</strong> {csvColumns.length}
                    </div>
                  </Alert>
                ) : (
                  <Alert variant="secondary" className="mb-0">
                    <FontAwesomeIcon icon={faUpload} className="mr-2" />
                    Sube un archivo <code>.csv</code> para habilitar el mapeo de columnas.
                  </Alert>
                )}
              </Col>
            </Row>

            {csvColumns.length > 0 && (
              <>
                <hr />
                <h6 className="font-weight-bold mb-3">
                  Mapeo de columnas del CSV
                </h6>
                <Row>
                  {MANDATORY_IMPORT_FIELDS.map((fieldItem) => (
                    <Col md={4} key={fieldItem.key} className="mb-3">
                      <Form.Group controlId={`map-${fieldItem.key}`}>
                        <Form.Label>
                          {fieldItem.label}
                          <strong className="text-danger">*</strong>
                        </Form.Label>
                        <Form.Control
                          as="select"
                          value={fieldMapping[fieldItem.key]}
                          onChange={(event) =>
                            handleMappingChange(fieldItem.key, event.target.value)
                          }
                          disabled={isImporting}
                          required
                        >
                          <option value="">Seleccionar columna...</option>
                          {csvColumns.map((columnItem) => (
                            <option
                              key={columnItem.index}
                              value={columnItem.index}
                            >
                              {columnItem.label}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
                <Row>
                  {OPTIONAL_IMPORT_FIELDS.map((fieldItem) => (
                    <Col md={4} key={fieldItem.key} className="mb-3">
                      <Form.Group controlId={`map-${fieldItem.key}`}>
                        <Form.Label>
                          {fieldItem.label}{' '}
                          <span className="text-muted small">(Opcional)</span>
                        </Form.Label>
                        <Form.Control
                          as="select"
                          value={fieldMapping[fieldItem.key]}
                          onChange={(event) =>
                            handleMappingChange(fieldItem.key, event.target.value)
                          }
                          disabled={isImporting}
                        >
                          <option value="">No mapear / Vacío</option>
                          {csvColumns.map((columnItem) => (
                            <option
                              key={columnItem.index}
                              value={columnItem.index}
                            >
                              {columnItem.label}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  ))}
                </Row>

                {mappedRows.length > 0 && (
                  <div className="mt-2">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="font-weight-bold small text-secondary">
                        Vista previa de datos normalizados (Primeras{' '}
                        {Math.min(5, mappedRows.length)} de {mappedRows.length}{' '}
                        filas — Nombre y Apellido capitalizados automáticamente)
                      </span>
                    </div>
                    <Table striped bordered hover size="sm" responsive className="mb-0">
                      <thead>
                        <tr>
                          <th>Fila</th>
                          <th>Nombre (firstName)</th>
                          <th>Apellido (lastName)</th>
                          <th>E-Mail</th>
                          <th>D.N.I.</th>
                          <th>Teléfono</th>
                          <th>Validación</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mappedRows.slice(0, 5).map((previewRow) => (
                          <tr key={previewRow.rowNumber}>
                            <td>{previewRow.rowNumber}</td>
                            <td>{previewRow.firstName || '-'}</td>
                            <td>{previewRow.lastName || '-'}</td>
                            <td>{previewRow.email || '-'}</td>
                            <td>{previewRow.dni || '-'}</td>
                            <td>{previewRow.phone || '-'}</td>
                            <td>
                              {previewRow.isValid ? (
                                <Badge variant="success">OK</Badge>
                              ) : (
                                <Badge variant="danger">
                                  Falta: {previewRow.missingFields.join(', ')}
                                </Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body>
            <h5 className="mb-3">
              2. Comisión, plan de pagos y estado (aplica a todos los alumnos)
            </h5>

            <Row>
              <Col md={4}>
                <Form.Group controlId="importClassroomYear">
                  <Form.Label>
                    Año<strong className="text-danger">*</strong>:
                  </Form.Label>
                  <Form.Control
                    type="number"
                    value={classroomYear}
                    onChange={(event) => setClassroomYear(event.target.value)}
                    min="2019"
                    max={currentYear + 1}
                    disabled={isImporting}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="importCountry">
                  <Form.Label>País:</Form.Label>
                  <CountryDropdown
                    className="customSelect"
                    name="country"
                    defaultOptionLabel="Seleccionar país"
                    showDefaultOption="Argentina"
                    priorityOptions={['AR']}
                    value={country}
                    onChange={(selectedCountry) => setCountry(selectedCountry)}
                    disabled={isImporting}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="importRegion">
                  <Form.Label>Provincia:</Form.Label>
                  <RegionDropdown
                    className="customSelect"
                    country={country}
                    defaultOptionLabel="Seleccionar region"
                    value={region}
                    onChange={(selectedRegion) => setRegion(selectedRegion)}
                    disabled={isImporting}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group controlId="importAdditionalNotes">
                  <Form.Label>Notas adicionales:</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Notas adicionales para los alumnos importados"
                    value={additionalNotes}
                    onChange={(event) => setAdditionalNotes(event.target.value)}
                    disabled={isImporting}
                  />
                </Form.Group>
              </Col>
            </Row>

            <hr />

            <CardGroup>
              <Row className="g-3 w-100">
                {cardIndices.map((cardIdx) => (
                  <Col key={cardIdx} xs={12} md={4}>
                    <CourseCard
                      course={courseConfig.course}
                      classroom={courseConfig.classroom}
                      monthlyInstallments={courseConfig.monthlyInstallments}
                      price={courseConfig.price}
                      isActive={courseConfig.isActive}
                      downReason={courseConfig.downReason}
                      otherDownReason={courseConfig.otherDownReason}
                      moduleDownReason={courseConfig.moduleDownReason}
                      commissionChangedReason={
                        courseConfig.commissionChangedReason
                      }
                      otherCommissionChangedReason={
                        courseConfig.otherCommissionChangedReason
                      }
                      moduleCommissionChangedReason={
                        courseConfig.moduleCommissionChangedReason
                      }
                      scholarship={courseConfig.scholarship}
                      classroomList={activeClassrooms}
                      cardId={cardIdx}
                      handleSave={(cardData) =>
                        handleCardSave(cardData, cardIdx)
                      }
                      handleClose={() => handleCardClose(cardIdx)}
                      currency={courseConfig.currency}
                      plan={courseConfig.plan}
                      onToggleShowFinished={handleToggleShowFinished}
                    />
                  </Col>
                ))}
              </Row>
            </CardGroup>

            <Row className="mt-2">
              <Col>
                <Button
                  variant="info"
                  className="m-0 w-100"
                  disabled={isImporting}
                  onClick={() =>
                    setCourseQuantity((previousQty) => previousQty + 1)
                  }
                >
                  Agregar curso
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body>
            <h5 className="mb-3">3. Opciones de ejecución</h5>
            <Row className="align-items-end">
              <Col md={4}>
                <Form.Group controlId="importDelaySeconds" className="mb-md-0">
                  <Form.Label>
                    Demora entre cada creación (segundos)
                    <strong className="text-danger">*</strong>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    max={120}
                    step={1}
                    value={delaySeconds}
                    onChange={(event) => setDelaySeconds(event.target.value)}
                    disabled={isImporting}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Button
                  variant="info"
                  type="submit"
                  className="w-100"
                  disabled={!canStartImport}
                >
                  <FontAwesomeIcon icon={faPlay} className="mr-2" />
                  {isImporting
                    ? 'Importando alumnos...'
                    : `Importar ${mappedRows.length} alumno(s)`}
                </Button>
              </Col>
              <Col md={4} className="mt-2 mt-md-0">
                <Button
                  variant="danger"
                  type="button"
                  className="w-100"
                  disabled={isImporting}
                  onClick={() => history.push('/students/viewAll')}
                >
                  <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />
                  Cancelar
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Form>

      {(isImporting || importFinished) && (
        <Card className="shadow-sm mb-4 import-progress-card">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="import-progress-badge">
                {isImporting && (
                  <Spinner
                    animation="border"
                    variant="info"
                    size="sm"
                    className="mr-2"
                  />
                )}
                <h5 className="mb-0 text-dark">
                  {processedCount} de {totalToProcess} estudiantes procesados...
                </h5>
              </div>
              <Badge variant={isImporting ? 'info' : wasCancelled ? 'warning' : 'success'} className="p-2">
                {progressPercentage}%
              </Badge>
            </div>

            <ProgressBar
              animated={isImporting}
              striped={isImporting}
              variant={wasCancelled ? 'warning' : 'info'}
              now={progressPercentage}
              label={`${progressPercentage}%`}
              className="mb-3"
              style={{ height: '22px' }}
            />

            <div className="d-flex flex-wrap justify-content-between align-items-center small text-secondary">
              <div>
                <strong>Estado actual:</strong> {statusPhaseText}
                {isImporting && currentStudentLabel && (
                  <span className="ml-2">
                    — <strong>Alumno:</strong> {currentStudentLabel}
                  </span>
                )}
              </div>
              <div>
                <Badge variant="success" className="mr-2 p-1 px-2">
                  Creados: {createdCount}
                </Badge>
                <Badge variant="danger" className="mr-2 p-1 px-2">
                  Fallidos / Omitidos: {failedOrSkippedCount}
                </Badge>
                {cancelledCount > 0 && (
                  <Badge variant="secondary" className="p-1 px-2">
                    Cancelados: {cancelledCount}
                  </Badge>
                )}
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {importFinished && importResults.length > 0 && (
        <Card className="shadow-sm mb-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="text-success mr-2"
                />
                Resumen de la importación
              </h5>
              <div>
                <Badge variant="success" className="mr-2 p-2">
                  Creados: {createdCount}
                </Badge>
                <Badge variant="danger" className="mr-2 p-2">
                  Con error / Omitidos: {failedOrSkippedCount}
                </Badge>
                {cancelledCount > 0 && (
                  <Badge variant="secondary" className="p-2">
                    Sin procesar: {cancelledCount}
                  </Badge>
                )}
              </div>
            </div>

            <div className="import-summary-table-wrapper">
              <Table striped bordered hover size="sm" responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Fila</th>
                    <th>Nombre</th>
                    <th>Apellido</th>
                    <th>E-Mail</th>
                    <th>D.N.I.</th>
                    <th>Teléfono</th>
                    <th>Resultado</th>
                    <th>Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {importResults.map((resultItem) => (
                    <tr key={resultItem.rowNumber}>
                      <td>{resultItem.rowNumber}</td>
                      <td>{resultItem.firstName || '-'}</td>
                      <td>{resultItem.lastName || '-'}</td>
                      <td>{resultItem.email || '-'}</td>
                      <td>{resultItem.dni || '-'}</td>
                      <td>{resultItem.phone || '-'}</td>
                      <td>
                        {resultItem.status === 'created' && (
                          <Badge variant="success">Creado</Badge>
                        )}
                        {resultItem.status === 'skipped' && (
                          <Badge variant="warning">Omitido (Falló)</Badge>
                        )}
                        {resultItem.status === 'failed' && (
                          <Badge variant="danger">Error</Badge>
                        )}
                        {resultItem.status === 'cancelled' && (
                          <Badge variant="secondary">Cancelado</Badge>
                        )}
                      </td>
                      <td>
                        {resultItem.message}
                        {resultItem.studentId && (
                          <Link
                            to={`/students/details/${resultItem.studentId}`}
                            className="ml-2 font-weight-bold"
                          >
                            Ver alumno
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      <Modal
        show={Boolean(failedRowModal)}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header>
          <Modal.Title className="text-danger">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
            Error al importar estudiante
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {failedRowModal && (
            <>
              <p className="mb-2">
                Ocurrió un problema al procesar el registro{' '}
                <strong>
                  {failedRowModal.currentIndex} de {failedRowModal.totalRows}
                </strong>{' '}
                (Fila {failedRowModal.rowNumber} del CSV):
              </p>
              <Alert variant="light" className="border mb-3">
                <div>
                  <strong>Alumno:</strong> {failedRowModal.firstName}{' '}
                  {failedRowModal.lastName}
                </div>
                <div>
                  <strong>E-Mail:</strong> {failedRowModal.email || 'Sin email'}
                </div>
              </Alert>
              <Alert variant="danger" className="mb-2">
                <strong>Detalle del error:</strong> {failedRowModal.errorMessage}
              </Alert>
              <p className="mb-0 text-muted small">
                ¿Deseas omitir este alumno y continuar con los siguientes, o cancelar toda la operación?
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            onClick={() => handleFailureModalDecision('cancel')}
          >
            <FontAwesomeIcon icon={faStopCircle} className="mr-2" />
            Cancelar toda la operación
          </Button>
          <Button
            variant="primary"
            onClick={() => handleFailureModalDecision('skip')}
          >
            <FontAwesomeIcon icon={faForward} className="mr-2" />
            Omitir y continuar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default StudentImportCsv;
